import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { finance, type FinancePeriodKey, type StudyFinanceCardRow } from '@/api/client';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { StudyFinanceCard } from '@/components/finance/StudyFinanceCard';
import { PastInvoicesModal } from '@/components/finance/PastInvoicesModal';
import { VisitBillingDetailModal } from '@/components/finance/VisitBillingDetailModal';
import { GenerateInvoiceModal } from '@/pages/finance/GenerateInvoiceModal';

/**
 * DREAM39 — Financial Overview page. Per Jawahar's 2026-07-31 comment, this
 * is one card per study for a selected time period (Month / Year /
 * custom), not site-wide tiles + a flat invoice table. Every value on the
 * page is scoped to the active session site_id — the backend derives it
 * from the JWT, never a query param.
 */
const PERIOD_OPTIONS: { key: FinancePeriodKey; label: string }[] = [
  { key: 'month', label: 'This month' },
  { key: 'year', label: 'This year' },
  { key: 'custom', label: 'Custom range' },
];

export function FinancialOverviewPage() {
  const { user } = useAuth();
  const [periodKey, setPeriodKey] = useState<FinancePeriodKey>('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const cards = useQuery({
    queryKey: ['finance-study-cards', user?.activeSiteId, periodKey, customFrom, customTo],
    queryFn: () =>
      finance.studyCards({
        period: periodKey,
        from: periodKey === 'custom' ? customFrom || undefined : undefined,
        to: periodKey === 'custom' ? customTo || undefined : undefined,
      }),
    enabled: !!user?.activeSiteId,
  });

  const [invoiceModalStudy, setInvoiceModalStudy] = useState<StudyFinanceCardRow | null>(null);
  const [pastInvoicesStudy, setPastInvoicesStudy] = useState<StudyFinanceCardRow | null>(null);
  const [billingDetailStudy, setBillingDetailStudy] = useState<StudyFinanceCardRow | null>(null);

  const period = cards.data?.period;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Financial overview"
        subtitle={`Study billing at ${user?.siteNames?.[user.activeSiteId] ?? 'this site'}.`}
      />

      <div className="card flex flex-wrap items-center gap-3 p-3">
        <span className="text-xs uppercase tracking-wider text-ink-muted">Period</span>
        <div className="flex flex-wrap gap-1">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={periodKey === opt.key ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
              onClick={() => setPeriodKey(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {periodKey === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" className="input" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            <span className="text-ink-muted text-xs">to</span>
            <input type="date" className="input" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </div>
        )}
        {period && (
          <span className="text-xs text-ink-muted ml-auto">
            {period.from} – {period.to}
          </span>
        )}
      </div>

      {cards.isLoading ? (
        <div className="flex items-center justify-center py-16 text-ink-muted">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : cards.isError ? (
        <div className="card p-6 flex items-center gap-2 text-danger-700">
          <AlertTriangle className="h-4 w-4" /> Failed to load financial data.
        </div>
      ) : (cards.data?.cards.length ?? 0) === 0 ? (
        <EmptyState
          title="No studies to show"
          description="Active or completed studies at this site will appear here once they have visit activity."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {cards.data!.cards.map((card) => (
            <StudyFinanceCard
              key={card.study_id}
              card={card}
              onGenerateInvoice={() => setInvoiceModalStudy(card)}
              onViewPastInvoices={() => setPastInvoicesStudy(card)}
              onViewBillingDetail={() => setBillingDetailStudy(card)}
            />
          ))}
        </div>
      )}

      {invoiceModalStudy && period && (
        <GenerateInvoiceModal
          open
          onOpenChange={(o) => !o && setInvoiceModalStudy(null)}
          studyId={invoiceModalStudy.study_id}
          studyLabel={`${invoiceModalStudy.study_reference_number} – ${invoiceModalStudy.sponsor_name}`}
          defaultFrom={period.from}
          defaultTo={period.to}
        />
      )}
      {pastInvoicesStudy && (
        <PastInvoicesModal
          open
          onOpenChange={(o) => !o && setPastInvoicesStudy(null)}
          studyId={pastInvoicesStudy.study_id}
          studyLabel={`${pastInvoicesStudy.study_reference_number} – ${pastInvoicesStudy.sponsor_name}`}
        />
      )}
      {billingDetailStudy && period && (
        <VisitBillingDetailModal
          open
          onOpenChange={(o) => !o && setBillingDetailStudy(null)}
          studyId={billingDetailStudy.study_id}
          studyLabel={`${billingDetailStudy.study_reference_number} – ${billingDetailStudy.sponsor_name}`}
          from={period.from}
          to={period.to}
        />
      )}
    </div>
  );
}
